import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { Suspense, useEffect } from "react";
import { ScrollView } from "react-native";
import {
  PreloadedQuery,
  RelayEnvironmentProvider,
  usePreloadedQuery,
  useQueryLoader,
  useRelayEnvironment,
} from "react-relay";

import { WithRealSafeAreaInsets } from "@/hooks/useRealSafeAreaInsets";
import { Loader } from "@whoosh/design-system/src/components/display";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import { Box, Text } from "@whoosh/design-system/src/components/primitive";

import { ManageClubsModalQuery } from "./__generated__/ManageClubsModalQuery.graphql";
import { ClubRow } from "./components/ClubRow";

type MangeClubsModalProps = {
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
};

export const ManageClubsModal = ({
  isOpen,
  setIsOpen,
}: MangeClubsModalProps) => {
  const [queryRef, loadQuery] = useQueryLoader<ManageClubsModalQuery>(
    MangeClubsModalQueryString
  );

  useEffect(() => {
    loadQuery({});
  }, [loadQuery]);

  const environment = useRelayEnvironment();

  return (
    <WithRealSafeAreaInsets>
      <StandardModal
        upperTitle={t`Manage Clubs`}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        content={
          <RelayEnvironmentProvider environment={environment}>
            <Box flex={1} pl={6} pr={{ md: 6 }} pb={6}>
              <Suspense fallback={<Loader />}>
                {queryRef ? (
                  <ManageClubsModalInner queryRef={queryRef} />
                ) : null}
              </Suspense>
            </Box>
          </RelayEnvironmentProvider>
        }
      />
    </WithRealSafeAreaInsets>
  );
};

type ManageClubsModalInnerProps = {
  queryRef: PreloadedQuery<ManageClubsModalQuery>;
};

const ManageClubsModalInner = ({ queryRef }: ManageClubsModalInnerProps) => {
  const data = usePreloadedQuery(MangeClubsModalQueryString, queryRef);

  const shownProfiles = data?.session?.user?.memberProfiles?.edges?.filter(
    (mp) => !mp?.node?.hidden
  );

  const hiddenProfiles =
    data?.session?.user?.memberProfiles?.edges?.filter(
      (mp) => !!mp?.node?.hidden
    ) ?? [];

  return (
    <ScrollView style={{ flex: 1 }}>
      {shownProfiles?.map((mp, i) => {
        return mp?.node ? <ClubRow key={i} fragmentRef={mp?.node} /> : null;
      })}
      {hiddenProfiles.length ? (
        <>
          <Box mb={4} mt={8}>
            <Text>{t`Hidden`}</Text>
          </Box>
          {hiddenProfiles.map((mp, i) => {
            return mp?.node ? <ClubRow key={i} fragmentRef={mp?.node} /> : null;
          })}
        </>
      ) : null}
    </ScrollView>
  );
};

export const MangeClubsModalQueryString = graphql`
  query ManageClubsModalQuery {
    session {
      user {
        ... on MemberUser {
          memberProfiles {
            edges {
              node {
                ...ClubRow_MemberProfile
                club {
                  id
                  slug
                  name
                  logoImage {
                    # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
                    publicId: renderingKey
                  }
                }
                hidden
              }
            }
          }
        }
      }
    }
  }
`;
